// Hook for MediaRecorder with chunked output

import { useState, useRef, useCallback, useEffect } from 'react'

interface UseMediaRecorderOptions {
  stream: MediaStream | null
  onChunk: (chunk: Blob, chunkNumber: number) => void
  chunkInterval?: number // milliseconds (default: 5000)
}

interface UseMediaRecorderReturn {
  isRecording: boolean
  startRecording: () => void
  stopRecording: () => void
  chunkCount: number
  mimeType: string
}

function getSupportedMimeType(): string {
  // Detect mobile devices - need MP4 for Photos/Gallery app compatibility
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

  // Mobile: prioritize MP4 (saves to Photos/Gallery)
  // Desktop: prioritize webm (better compression, Firefox compatibility)
  const types = isMobile
    ? [
        'video/mp4',
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/webm',
      ]
    : [
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/webm',
        'video/mp4',
      ]

  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type
    }
  }

  // Fallback - let browser decide
  return ''
}

// Get file extension from mime type
export function getExtensionFromMimeType(mimeType: string): string {
  if (mimeType.startsWith('video/mp4')) return 'mp4'
  if (mimeType.startsWith('video/webm')) return 'webm'
  return 'webm' // default fallback
}

export function useMediaRecorder({
  stream,
  onChunk,
  chunkInterval = 5000,
}: UseMediaRecorderOptions): UseMediaRecorderReturn {
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunkNumberRef = useRef(0)
  const [isRecording, setIsRecording] = useState(false)
  const [chunkCount, setChunkCount] = useState(0)
  const [mimeType, setMimeType] = useState('')

  const startRecording = useCallback(() => {
    if (!stream) {
      console.error('Cannot start recording: no stream available')
      return
    }

    const detectedMimeType = getSupportedMimeType()
    setMimeType(detectedMimeType)

    const options: MediaRecorderOptions = {
      videoBitsPerSecond: 1500000, // 1.5 Mbps
    }

    if (detectedMimeType) {
      options.mimeType = detectedMimeType
    }

    try {
      const recorder = new MediaRecorder(stream, options)
      chunkNumberRef.current = 0
      setChunkCount(0)

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunkNumberRef.current++
          setChunkCount(chunkNumberRef.current)
          onChunk(event.data, chunkNumberRef.current)
        }
      }

      recorder.onerror = (event) => {
        console.error('MediaRecorder error:', event)
      }

      recorder.start(chunkInterval)
      recorderRef.current = recorder
      setIsRecording(true)
    } catch (err) {
      console.error('Failed to start MediaRecorder:', err)
    }
  }, [stream, onChunk, chunkInterval])

  const stopRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop()
    }
    recorderRef.current = null
    setIsRecording(false)
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop()
      }
    }
  }, [])

  return {
    isRecording,
    startRecording,
    stopRecording,
    chunkCount,
    mimeType,
  }
}
